import { useState, useEffect, useRef } from "react";
import { Share2, Facebook, MessageCircle, Mail, Link2, Check, X } from "lucide-react";
import { toast } from "sonner";

const SHARE_TITLE = "McFly's Fuel Inc. — Heating Oil Delivery on Long Island";
const SHARE_TEXT =
  "Reliable, affordable heating oil delivery on Long Island. Fast service, honest pricing — call 631-372-3526.";

export default function ShareButton() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);
  const popoverRef = useRef(null);

  const url = typeof window !== "undefined" ? window.location.origin : "https://mcflysfuel.com";
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(SHARE_TEXT);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    function onClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: SHARE_TITLE, text: SHARE_TEXT, url });
        setOpen(false);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  };

  const handleMainClick = async () => {
    const shared = await handleNativeShare();
    if (!shared) setOpen((v) => !v);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const items = [
    {
      label: "Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "#1877F2",
    },
    {
      label: "SMS",
      Icon: MessageCircle,
      href: `sms:?&body=${encodedText}%20${encodedUrl}`,
      color: "#34B7F1",
    },
    {
      label: "Email",
      Icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(SHARE_TITLE)}&body=${encodedText}%20${encodedUrl}`,
      color: "#D00017",
    },
  ];

  return (
    <div
      className={`fixed bottom-40 sm:bottom-44 right-5 sm:right-7 z-50 transition-all ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      ref={popoverRef}
    >
      {open && (
        <div
          className="mb-3 bg-white border-2 border-brand-navy shadow-[5px_5px_0_#0A1128] p-3 w-60 relative"
          data-testid="share-popover"
        >
          <button
            onClick={() => setOpen(false)}
            className="absolute -top-2 -right-2 bg-brand-navy text-white w-6 h-6 flex items-center justify-center border-2 border-white"
            aria-label="Close share menu"
            data-testid="share-close"
          >
            <X size={12} />
          </button>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-brand-red mb-2">Share McFly's</div>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {items.map(({ label, Icon, href, color }) => (
              <a
                key={label}
                href={href}
                target={label === "Facebook" ? "_blank" : undefined}
                rel={label === "Facebook" ? "noreferrer" : undefined}
                onClick={() => setOpen(false)}
                className="flex flex-col items-center gap-1 p-2 border border-brand-navy/20 hover:bg-brand-navy/5 transition-colors"
                data-testid={`share-${label.toLowerCase()}`}
                title={`Share via ${label}`}
              >
                <span
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white"
                  style={{ background: color }}
                >
                  <Icon size={16} />
                </span>
                <span className="text-[10px] uppercase tracking-wider text-brand-navy font-bold">{label}</span>
              </a>
            ))}
          </div>
          <button
            onClick={copyLink}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-brand-navy text-white hover:bg-brand-navyLight font-heading tracking-wide text-sm"
            data-testid="share-copy-link"
          >
            <span className="flex items-center gap-2">
              {copied ? <Check size={14} className="text-brand-gold" /> : <Link2 size={14} />}
              {copied ? "Copied!" : "Copy link"}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-brand-gold">
              {url.replace(/^https?:\/\//, "").slice(0, 16)}…
            </span>
          </button>
        </div>
      )}

      <button
        onClick={handleMainClick}
        className="group inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navyLight text-white px-3 sm:px-4 py-2.5 sm:py-3 font-heading tracking-wide border-2 border-brand-navy shadow-[5px_5px_0_#D00017] hover:shadow-[8px_8px_0_#D00017] hover:-translate-x-1 hover:-translate-y-1 transition-all"
        data-testid="share-us-btn"
        aria-label="Share McFly's Fuel Inc."
      >
        <Share2 size={16} className="text-brand-gold" />
        <span className="text-xs sm:text-sm">SHARE</span>
      </button>
    </div>
  );
}
